import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ChefHat, CalendarRange, CalendarClock, CalendarCheck, PartyPopper, Check, Lock } from "lucide-react";
import { BrandMark } from "@/components/Brand";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const SERVICES = [
  { id: "monthly",  title: "Monthly",  icon: CalendarRange, blurb: "Daily home meals, fixed 4-week cycle" },
  { id: "one-time", title: "One-Time", icon: CalendarClock, blurb: "Single occasion — gathering or one-off meal" },
  { id: "party",    title: "Party",    icon: PartyPopper,   blurb: "Larger event — birthday, anniversary, get-together" },
  { id: "custom",   title: "Custom",   icon: CalendarCheck, blurb: "Pick your own date range and frequency" },
];

const MEALS = ["breakfast", "lunch", "dinner"];

// Service is locked if there's an existing lead in any non-terminal status (everything except "rejected").
const LOCKING_STATUSES = ["new", "request_submitted", "trial_scheduled", "payment_pending", "payment_complete", "payment_done", "pushed_to_customer", "contacted", "trial_done", "converted"];

export default function NewCustomerJourney() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState(null);
  const [step, setStep] = useState("pick"); // pick | form | profile | done
  const [busy, setBusy] = useState(false);
  const [lockedServices, setLockedServices] = useState({});  // {monthly: "trial_scheduled", ...}

  const [form, setForm] = useState({
    start_date: "", end_date: "",
    service_date: "", service_time: "", venue: "",
    date_range_start: "", date_range_end: "",
    meals: [], total_persons: 2,
    dietary: "veg", allergies: "", special_instructions: "",
  });

  // Profile step (collected after the lead submits successfully).
  // Pre-fills dietary from the lead form. Matches CustomerOnboarding API payload.
  const [profile, setProfile] = useState({
    name: "",
    address: "", city: "Noida",
    dietary: "veg",
  });

  // Pre-fill from the currently-logged-in user so we don't wipe an existing name/address.
  useEffect(() => {
    if (!user) return;
    setProfile(p => ({
      ...p,
      name: p.name || (user.name && !user.name.startsWith("Guest ") ? user.name : ""),
      address: p.address || user.profile?.address || "",
      city: user.profile?.city || p.city,
      dietary: user.profile?.dietary || p.dietary,
    }));
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customer/leads");
        const locks = {};
        for (const l of data) {
          if (LOCKING_STATUSES.includes(l.status)) {
            // Only first / latest active lead per service type
            if (!locks[l.service_type]) locks[l.service_type] = l.status;
          }
        }
        setLockedServices(locks);
      } catch { /* ignore */ }
    })();
  }, []);

  const toggleMeal = (m) => setForm(f => ({ ...f, meals: f.meals.includes(m) ? f.meals.filter(x=>x!==m) : [...f.meals, m] }));

  const submit = async (e) => {
    e.preventDefault();
    if (service === "monthly") {
      if (!form.start_date || !form.end_date) { toast.error("Pick start and end dates"); return; }
      if (form.meals.length === 0) { toast.error("Select at least one meal"); return; }
    }
    if (service === "one-time") {
      if (!form.service_date || !form.service_time || !form.venue) { toast.error("Fill date, time and venue"); return; }
    }
    if (service === "custom") {
      if (!form.date_range_start || !form.date_range_end) { toast.error("Pick date range"); return; }
    }
    setBusy(true);
    const payload = { service_type: service, total_persons: Number(form.total_persons),
      dietary: form.dietary, allergies: form.allergies, special_instructions: form.special_instructions };
    if (service === "monthly") Object.assign(payload, { start_date: form.start_date, end_date: form.end_date, meals: form.meals });
    if (service === "one-time") Object.assign(payload, { service_date: form.service_date, service_time: form.service_time, venue: form.venue });
    if (service === "custom") Object.assign(payload, { date_range_start: form.date_range_start, date_range_end: form.date_range_end });
    try {
      await api.post("/customer/leads", payload);
      // Lead saved — collect remaining profile details before showing the thank-you screen.
      setProfile(p => ({ ...p, dietary: form.dietary }));
      setStep("profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit request");
    } finally {
      setBusy(false);
    }
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { toast.error("Please tell us your name"); return; }
    if (!profile.address.trim()) { toast.error("Please share your address"); return; }
    setBusy(true);
    try {
      await api.post("/customer/onboarding", {
        name: profile.name.trim(),
        address: profile.address,
        city: profile.city,
        household_size: 1,
        dietary: profile.dietary,
        allergies: [],
      });
      await refresh();
      toast.success("Profile saved");
      nav("/app?tab=requests");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center"><BrandMark size={32}/></div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground hidden sm:inline">Hi, {user?.name?.split(" ")[0]}</span>
            <Button variant="outline" size="sm" className="rounded-full" onClick={async () => { await logout(); nav("/"); }} data-testid="onb-logout">Sign out</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {step === "profile" ? (
          <form onSubmit={submitProfile} className="card-default space-y-6" data-testid="profile-form">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Almost done</p>
              <h1 className="heading text-3xl sm:text-4xl font-bold">Tell us about your kitchen</h1>
              <p className="text-base text-muted-foreground mt-2">Quick details so we can match the right chef and plan delivery routes.</p>
            </div>

            <div>
              <Label>Your name <span className="text-rose-500">*</span></Label>
              <Input
                value={profile.name}
                onChange={e=>setProfile({...profile, name: e.target.value})}
                placeholder="Full name"
                autoComplete="name"
                required
                data-testid="profile-name"
              />
            </div>

            <div>
              <Label>Address <span className="text-rose-500">*</span></Label>
              <AddressAutocomplete
                value={profile.address}
                onChange={(v)=>setProfile({...profile, address: v})}
                onSelect={({ address })=>setProfile({...profile, address })}
                placeholder="Flat / Building / Sector / Landmark"
                multiline
                required
                data-testid="profile-address"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={profile.city}
                  onChange={e=>setProfile({...profile, city: e.target.value})}
                  placeholder="Noida, UP 201301"
                  data-testid="profile-city"
                />
              </div>
              <div>
                <Label className="mb-2 block">Dietary preference</Label>
                <RadioGroup value={profile.dietary} onValueChange={(v)=>setProfile({...profile, dietary: v})} className="flex gap-6 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="veg" data-testid="profile-veg"/><span>Vegetarian</span></label>
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="non-veg" data-testid="profile-nonveg"/><span>Non-vegetarian</span></label>
                </RadioGroup>
              </div>
            </div>

            <Button type="submit" disabled={busy} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="submit-profile">
              {busy ? "Saving…" : "Save & continue"}
            </Button>
          </form>
        ) : step === "done" ? (
          <div className="card-default text-center py-12 space-y-4" data-testid="lead-done">
            <div className="w-16 h-16 mx-auto rounded-full bg-secondary/15 flex items-center justify-center">
              <Check size={28} className="text-secondary"/>
            </div>
            <h1 className="heading text-3xl font-bold">Thank you for your response.</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Our team will get in touch shortly to walk you through the next steps and arrange a trial.
            </p>
            <div className="pt-2 flex gap-3 justify-center">
              <Button variant="outline" className="rounded-full" onClick={()=>{ setStep("pick"); setService(null); }} data-testid="another-req">Submit another request</Button>
              <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={()=>nav("/app")} data-testid="go-app">View my requests</Button>
            </div>
          </div>
        ) : step === "pick" ? (
          <div className="space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Step 1 of 2</p>
              <h1 className="heading text-4xl sm:text-5xl font-bold">What service do you need?</h1>
              <p className="text-base text-muted-foreground mt-2">Pick one — you can always change later when our team reaches out.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {SERVICES.map(s => {
                const Icon = s.icon;
                const active = service === s.id;
                const locked = !!lockedServices[s.id];
                return (
                  <button
                    key={s.id} type="button"
                    onClick={()=> locked ? toast.info(`You already have a ${s.title.toLowerCase()} request in progress.`) : setService(s.id)}
                    disabled={locked}
                    data-testid={`svc-${s.id}`}
                    className={`relative text-left p-5 rounded-2xl border-2 transition-all ${locked ? "border-border bg-muted/40 opacity-60 cursor-not-allowed" : active ? "border-primary bg-primary/5 shadow-md scale-[1.02]" : "border-border bg-card hover:border-primary/30 hover:shadow-sm"}`}
                  >
                    {locked && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-card border border-border rounded-full px-2 py-0.5">
                        <Lock size={10}/> In progress
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-full mb-3 flex items-center justify-center ${active && !locked ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      <Icon size={18}/>
                    </div>
                    <div className="heading text-lg font-semibold">{s.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.blurb}</div>
                  </button>
                );
              })}
            </div>
            <Button disabled={!service} onClick={()=>setStep("form")} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8" data-testid="continue-to-form">
              Continue
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="lead-form">
            <div>
              <button type="button" onClick={()=>setStep("pick")} className="text-sm text-muted-foreground hover:text-primary mb-2" data-testid="back-to-pick">← Back</button>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Step 2 of 2</p>
              <h1 className="heading text-3xl font-bold capitalize">{service} service details</h1>
            </div>

            <div className="card-default space-y-5">
              {service === "monthly" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Start date</Label>
                      <Input
                        type="date"
                        required
                        value={form.start_date}
                        onChange={e=>{
                          const sd = e.target.value;
                          // Auto-fill end_date = start + 27 days (locked 4-week cycle)
                          let ed = "";
                          if (sd) {
                            const d = new Date(sd);
                            d.setDate(d.getDate() + 27);
                            ed = d.toISOString().slice(0,10);
                          }
                          setForm({...form, start_date: sd, end_date: ed});
                        }}
                        data-testid="start-date"
                      />
                    </div>
                    <div>
                      <Label>End date <span className="text-xs text-muted-foreground">(auto-set, 4-week cycle)</span></Label>
                      <Input
                        type="date"
                        required
                        readOnly
                        value={form.end_date}
                        className="bg-muted/50 cursor-not-allowed"
                        data-testid="end-date"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">Monthly subscriptions follow a fixed 4-week (28-day) cycle.</p>
                  <div>
                    <Label>Meals required</Label>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {MEALS.map(m => {
                        const on = form.meals.includes(m);
                        return (
                          <button key={m} type="button" data-testid={`meal-${m}`} onClick={()=>toggleMeal(m)}
                            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border capitalize transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"}`}>
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {(service === "one-time" || service === "party") && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Service date</Label><Input type="date" required value={form.service_date} onChange={e=>setForm({...form, service_date: e.target.value})} data-testid="service-date"/></div>
                    <div><Label>Service time</Label><Input type="time" required value={form.service_time} onChange={e=>setForm({...form, service_time: e.target.value})} data-testid="service-time"/></div>
                  </div>
                  <div><Label>Venue / address</Label>
                    <AddressAutocomplete
                      value={form.venue}
                      onChange={(v)=>setForm({...form, venue: v})}
                      onSelect={({ address })=>setForm({...form, venue: address })}
                      placeholder="Complete address with sector / building"
                      multiline
                      required
                      data-testid="venue"
                    />
                  </div>
                </>
              )}

              {service === "custom" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>From</Label><Input type="date" required value={form.date_range_start} onChange={e=>setForm({...form, date_range_start: e.target.value})} data-testid="range-from"/></div>
                    <div><Label>To</Label><Input type="date" required value={form.date_range_end} min={form.date_range_start} onChange={e=>setForm({...form, date_range_end: e.target.value})} data-testid="range-to"/></div>
                  </div>
                </>
              )}

              <div>
                <Label>Total persons</Label>
                <Input type="number" min={1} max={50} required value={form.total_persons} onChange={e=>setForm({...form, total_persons: e.target.value})} data-testid="total-persons"/>
              </div>

              <div>
                <Label className="mb-2 block">Dietary preference</Label>
                <RadioGroup value={form.dietary} onValueChange={(v)=>setForm({...form, dietary: v})} className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="veg" data-testid="diet-veg"/><span>Vegetarian</span></label>
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="non-veg" data-testid="diet-nonveg"/><span>Non-vegetarian</span></label>
                </RadioGroup>
              </div>

              <div>
                <Label>Allergies</Label>
                <Input value={form.allergies} onChange={e=>setForm({...form, allergies: e.target.value})} placeholder="e.g. peanut, dairy, gluten" data-testid="allergies"/>
              </div>

              <div>
                <Label>Special instructions</Label>
                <Textarea value={form.special_instructions} onChange={e=>setForm({...form, special_instructions: e.target.value})} placeholder="Low oil, less spicy, specific dishes you love..." data-testid="special"/>
              </div>
            </div>

            <Button type="submit" disabled={busy} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="submit-lead">
              {busy ? "Submitting..." : "Submit request"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
